import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useLiveFindings } from '../api/live'
import { useAssignFinding, useFindings, type QueueFilters } from '../api/queries'
import { useTenant } from '../api/tenant'
import type { FindingSummary, Session } from '../api/types'
import { CloseDialog } from '../components/CloseDialog'
import { Failure } from '../components/Failure'
import { Preview } from '../components/Preview'
import { QueueList } from '../components/QueueList'
import { SavedViews } from '../components/SavedViews'

const SHORTCUTS =
  'j/k move · Enter open · p preview · x pick · a assign to me · c close · / filter'

/**
 * The triage queue.
 *
 * Selection state lives here rather than in the URL: moving the cursor is not
 * navigation, and putting it in history would make the back button walk backwards
 * through a list one row at a time.
 */
export function QueuePage({ session }: { session: Session }) {
  const navigate = useNavigate()
  // Seeded from the URL so that "show me the alerts for T1110.001" is a link another
  // screen can hand over, and a link an analyst can paste to a colleague. Read once:
  // after that the filter controls own the state, and rewriting the URL on every
  // keystroke would fill the back button with a partially-typed hostname.
  const [params] = useSearchParams()
  const [filters, setFilters] = useState<QueueFilters>(() => ({
    technique: params.get('technique') ?? undefined,
    rule_id: params.get('rule_id') ?? undefined,
    hostname: params.get('hostname') ?? undefined,
    status: params.get('status') ?? undefined,
    min_severity: params.get('min_severity') ?? undefined,
  }))
  const [cursor, setCursor] = useState(0)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [previewing, setPreviewing] = useState(false)
  const [closing, setClosing] = useState<FindingSummary[] | null>(null)
  const filterRef = useRef<HTMLInputElement>(null)

  const [tenant] = useTenant(session.memberships.map((membership) => membership.tenant))
  const query = useFindings({ ...filters, tenant: tenant || undefined })
  const live = useLiveFindings(query.isSuccess, () => query.refetch(), tenant || undefined)
  const assign = useAssignFinding()
  const rows = useMemo(() => query.data?.results ?? [], [query.data])
  const current = rows[cursor]

  // Keep the cursor inside the list when filters change the list under it.
  useEffect(() => {
    setCursor((index) => Math.min(index, Math.max(rows.length - 1, 0)))
  }, [rows.length])

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null
      // Never steal a key from someone who is typing. A queue that hijacks `c` while
      // an analyst writes a disposition reason is a queue they will stop using.
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return
      if (event.metaKey || event.ctrlKey || event.altKey) return

      const selected = rows[cursor]
      switch (event.key) {
        case 'j':
          event.preventDefault()
          setCursor((index) => Math.min(index + 1, rows.length - 1))
          break
        case 'k':
          event.preventDefault()
          setCursor((index) => Math.max(index - 1, 0))
          break
        case 'Enter':
          if (selected) navigate(`/findings/${selected.id}`)
          break
        case 'p':
          setPreviewing((shown) => !shown)
          break
        case 'x':
          if (selected) {
            setPicked((current) => {
              const next = new Set(current)
              if (!next.delete(selected.id)) next.add(selected.id)
              return next
            })
            setCursor((index) => Math.min(index + 1, rows.length - 1))
          }
          break
        case 'a':
          for (const finding of pickedOr(rows, picked, selected)) {
            assign.mutate({ id: finding.id, assignee: session.username })
          }
          break
        case 'c': {
          const batch = pickedOr(rows, picked, selected)
          if (batch.length > 0) setClosing(batch)
          break
        }
        case 'Escape':
          setPicked(new Set())
          break
        case '/':
          event.preventDefault()
          filterRef.current?.focus()
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [rows, cursor, picked, navigate, assign, session.username])

  return (
    <section aria-labelledby="queue-heading">
      <header
        style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h2 id="queue-heading" style={{ margin: 0 }}>
          Triage
        </h2>
        <span className="muted">{query.data ? `${query.data.count} open` : ''}</span>
        <span className="muted mono" style={{ marginLeft: 'auto' }}>
          {SHORTCUTS}
        </span>
      </header>

      {live.error !== null && (
        <p className="error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
          Live updates have stopped. This list may be out of date — reload to be sure.
        </p>
      )}

      {live.pending > 0 && (
        <button className="primary" style={{ marginTop: 'var(--space-3)' }} onClick={live.accept}>
          {live.pending} new alert{live.pending === 1 ? '' : 's'}
          {live.worstPending >= 4 ? ', including high severity' : ''} — show them
        </button>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)', margin: 'var(--space-4) 0' }}>
        <input
          ref={filterRef}
          aria-label="Filter by host"
          placeholder="host…"
          value={filters.hostname ?? ''}
          onChange={(event) =>
            setFilters((current) => ({ ...current, hostname: event.target.value || undefined }))
          }
        />
        <select
          aria-label="Minimum severity"
          value={filters.min_severity ?? ''}
          onChange={(event) =>
            setFilters((current) => ({ ...current, min_severity: event.target.value || undefined }))
          }
        >
          <option value="">Any severity</option>
          <option value="5">Critical only</option>
          <option value="4">High and above</option>
          <option value="3">Medium and above</option>
        </select>
        <select
          aria-label="Status"
          value={filters.status ?? ''}
          onChange={(event) =>
            setFilters((current) => ({ ...current, status: event.target.value || undefined }))
          }
        >
          <option value="">Open</option>
          <option value="closed">Closed</option>
          <option value="suppressed">Suppressed</option>
        </select>
      </div>

      {/* The filters that have no control of their own. A queue narrowed by something
          invisible looks exactly like a quiet night, which is the most expensive
          misreading this screen can produce. */}
      {(filters.technique || filters.rule_id) && (
        <p className="panel" role="status" style={{ marginTop: 0 }}>
          Filtered to <span className="mono">{filters.technique ?? filters.rule_id}</span> — this is
          a subset of the queue, not the whole of it.{' '}
          <button
            className="linklike"
            onClick={() =>
              setFilters((current) => ({ ...current, technique: undefined, rule_id: undefined }))
            }
          >
            Show everything
          </button>
        </p>
      )}

      <SavedViews filters={filters} onApply={setFilters} />

      {picked.size > 0 && (
        <p className="panel" style={{ marginTop: 'var(--space-3) ' }} role="status">
          {picked.size} picked — <kbd>c</kbd> to close them together, <kbd>a</kbd> to take
          them, <kbd>Esc</kbd> to clear.
        </p>
      )}

      {query.isError && <Failure error={query.error} onRetry={() => query.refetch()} />}

      {query.isLoading && (
        <div aria-hidden="true">
          {Array.from({ length: 8 }, (_, index) => (
            <div className="skeleton" key={index} />
          ))}
        </div>
      )}

      {query.isSuccess && rows.length === 0 && (
        <p className="empty">Nothing open. The queue is genuinely empty, not broken.</p>
      )}

      {query.isSuccess && rows.length > 0 && (
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-4)',
            gridTemplateColumns: previewing ? '1fr 380px' : '1fr',
            marginTop: 'var(--space-3)',
          }}
        >
          <QueueList
            rows={rows}
            cursor={cursor}
            selected={picked}
            onCursor={setCursor}
            onOpen={(finding) => navigate(`/findings/${finding.id}`)}
          />
          {/* The preview exists so that deciding "is this worth opening" does not cost
              the analyst their place in the list. */}
          {previewing && <Preview id={current?.id} />}
        </div>
      )}

      {closing && (
        <CloseDialog
          findings={closing}
          onDone={() => {
            setClosing(null)
            setPicked(new Set())
            query.refetch()
          }}
        />
      )}
    </section>
  )
}

/** The picked alerts, or the one under the cursor when nothing is picked. */
function pickedOr(
  rows: FindingSummary[],
  picked: Set<string>,
  current: FindingSummary | undefined,
): FindingSummary[] {
  if (picked.size > 0) return rows.filter((finding) => picked.has(finding.id))
  return current ? [current] : []
}
